import { put, takeLatest, select } from "redux-saga/effects";
import {
  fetchDataRequest,
  fetchDataSuccess,
  fetchDataFailure,
  fetchFiltersSuccess,
  fetchFiltersFailure,
  fetchFiltersRequest,
} from "./apiSlice";
import baseURL from "../../utils/config";

// Cache configuration
const CACHE_DURATION = 10 * 60 * 1000; 
const cache = {
  allData: null, 
  timestamp: null,
  filters: null, 
  searchQuery: null,
};

function isCacheValid(datasetName, searchQuery, filters) {
  const datasetCache = cache[datasetName];
  
  if (!datasetCache || !datasetCache.allData || !datasetCache.timestamp) {
    return false;
  }
  
  const now = Date.now();
  const isExpired = (now - datasetCache.timestamp) > CACHE_DURATION;
  
  if (isExpired) {
    console.log(`[CACHE] Cache expired for ${datasetName}, will fetch fresh data`);
    return false;
  }
  
  const currentFiltersStr = JSON.stringify(filters || null);
  const cachedFiltersStr = JSON.stringify(datasetCache.filters);
  const currentSearch = searchQuery || null;
  const cachedSearch = datasetCache.searchQuery;
  
  if (currentFiltersStr !== cachedFiltersStr || currentSearch !== cachedSearch) {
    console.log(`[CACHE] Different search/filters for ${datasetName}, will fetch fresh data`);
    return false;
  }
  
  const timeLeft = Math.round((CACHE_DURATION - (now - datasetCache.timestamp)) / 1000);
  console.log(`[CACHE] Using cached data for ${datasetName} (${datasetCache.allData.length} items), ${timeLeft}s until refresh`);
  return true;
}

function updateCache(datasetName, allData, searchQuery, filters) {
  if (!cache[datasetName]) {
    cache[datasetName] = {};
  }
  cache[datasetName].allData = allData;
  cache[datasetName].timestamp = Date.now();
  cache[datasetName].searchQuery = searchQuery || null;
  cache[datasetName].filters = filters || null;
  console.log(`[CACHE] Cache updated for ${datasetName} with ${allData.length} items, valid for 5 minutes`);
}

export function clearCache(datasetName = null) {
  if (datasetName) {
    delete cache[datasetName];
    console.log(`[CACHE] Cache cleared for ${datasetName}`);
  } else {
    Object.keys(cache).forEach(key => delete cache[key]);
    console.log('[CACHE] All caches cleared');
  }
}


function paginateData(data, page, pageSize) {
  const total = data.length;
  const totalPages = Math.ceil(total / pageSize);
  const startIdx = (page - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const paginatedData = data.slice(startIdx, endIdx);
  
  return {
    data: paginatedData,
    total,
    page,
    pageSize,
    totalPages
  };
}

function* fetchDataSaga(action) {
  const { datasetName, searchQuery, filters, page, pageSize, forceRefresh } = action.payload;
  
  try {
    const requestedPage = page || 1;
    const requestedPageSize = pageSize || 12;
    
    if (!forceRefresh && isCacheValid(datasetName, searchQuery, filters)) {
      console.log(`[SAGA] Serving page ${requestedPage} from cache for ${datasetName}`);
      

      const paginatedResult = paginateData(cache[datasetName].allData, requestedPage, requestedPageSize);
      
      yield put(fetchDataSuccess({
        datasetName,
        data: paginatedResult.data,
        total: paginatedResult.total,
        page: paginatedResult.page,
        totalPages: paginatedResult.totalPages
      }));
      return;
    }
    
    console.log('[SAGA] Fetching ALL solutions from backend...');
    
    const params = new URLSearchParams();
    if (searchQuery) params.append('search', searchQuery);
    if (filters && Object.keys(filters).length > 0) {
      params.append('filters', JSON.stringify(filters));
    }
    params.append('page', 1);
    params.append('page_size', 1000); 
    
    // Determine endpoint based on dataset name
    const endpoint = datasetName === "LAP Catalog" 
      // LAP Catalog now lists Dynamos (territories), not individual LAPs
      ? `${baseURL}/api/lap/lap_dynamos`
      : `${baseURL}/api/cassandra/solutions_catalog_live`;
    
    const url = `${endpoint}?${params.toString()}`;
    console.log(`[SAGA] Fetching ${datasetName} from:`, url);
    
    const response = yield fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const responseData = yield response.json();
    
    // Use backend total if available, otherwise use data length
    const totalFromBackend = responseData.total || responseData.data?.length || 0;
    
    updateCache(datasetName, responseData.data, searchQuery, filters);
    
    const paginatedResult = paginateData(responseData.data, requestedPage, requestedPageSize);
    
    yield put(fetchDataSuccess({
      datasetName,
      data: paginatedResult.data,
      total: totalFromBackend, // Use backend total for accurate count
      page: requestedPage,
      totalPages: Math.ceil(totalFromBackend / requestedPageSize)
    }));
  } catch (error) {
    console.error("Error in fetchDataSaga:", error);
    yield put(fetchDataFailure(error.message));
  }
}

function* fetchFilters(action) {
  const datasetName = action.payload;
  
  try {
    // For now, return empty filters since the backend doesn't have a filters endpoint
    // This can be enhanced later when filters are implemented
    const data = [];
    yield put(fetchFiltersSuccess({ datasetName, data }));
  } catch (error) {
    console.error("Error fetching filters:", error);
    yield put(fetchFiltersFailure(error.message));
  }
}

export function* watchDataSaga() {
  yield takeLatest(fetchDataRequest, fetchDataSaga);
  yield takeLatest(fetchFiltersRequest, fetchFilters);
}